/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Tag, Tabs, Button, Spin, InputNumber } from 'antd';
import { DownloadOutlined, ReloadOutlined, TableOutlined, BarChartOutlined } from '@ant-design/icons';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
    Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
    LabelList,
} from 'recharts';
import FleetService from '../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { TabPane } = Tabs;

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
    blue:   '#1a5b9a', orange: '#d46b08', green:  '#389e0d',
    red:    '#cf1322', purple: '#722ed1', teal:   '#13c2c2',
    amber:  '#faad14', grey:   '#595959',
};
const PIE_C = [C.blue, C.green, C.orange, C.red, C.purple, C.teal, C.amber, C.grey];

const toN = (v) => Number(v || 0);
const fmtE = (v) => `E ${toN(v).toLocaleString()}`;
const fmtKm = (v) => `${toN(v).toLocaleString()} km`;

// ─── RAG tag ──────────────────────────────────────────────────────────────────
const RAG = ({ val, okVal = 0, warnVal = null }) => {
    if (val === null || val === undefined) return <Tag>—</Tag>;
    if (typeof val === 'string') {
        const map = {
            Expired: 'error', 'Expiring Soon': 'warning', Valid: 'success',
            Serviceable: 'success', Unserviceable: 'error', TBC: 'default',
            Active: 'success', Inactive: 'default',
        };
        return <Tag color={map[val] || 'default'}>{val}</Tag>;
    }
    const n = Number(val);
    if (warnVal !== null) return <Tag color={n <= okVal ? 'success' : n <= warnVal ? 'warning' : 'error'}>{val}</Tag>;
    return <Tag color={n <= okVal ? 'success' : 'error'}>{val}</Tag>;
};

// ─── Export helper ────────────────────────────────────────────────────────────
const exportXlsx = (data, name) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, name);
    saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]),
        `${name}_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

// ─── Chart card wrapper ───────────────────────────────────────────────────────
const ChartCard = ({ title, children, height = 260 }) => (
    <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8,
        padding: '12px 16px', marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 12, color: '#1a3a5c', marginBottom: 10 }}>{title}</div>
        <div style={{ height }}>{children}</div>
    </div>
);

// ─── Toggle: Table | Charts ───────────────────────────────────────────────────
const ViewToggle = ({ view, onChange }) => (
    <div style={{ display: 'flex', border: '1px solid #e8e8e8', borderRadius: 6, overflow: 'hidden' }}>
        {[['table', <TableOutlined />, 'Table'], ['chart', <BarChartOutlined />, 'Charts']].map(([k, icon, label]) => (
            <button key={k} onClick={() => onChange(k)} style={{
                padding: '4px 12px', border: 'none', cursor: 'pointer', fontSize: 12,
                display: 'flex', alignItems: 'center', gap: 5,
                background: view === k ? '#1a3a5c' : '#fff',
                color: view === k ? '#fff' : '#666',
            }}>{icon}{label}</button>
        ))}
    </div>
);

// ─── Generic ReportTab with view toggle ──────────────────────────────────────
function ReportTab({ fetchFn, columns, exportName, renderCharts, params, children }) {
    const [data, setData]     = useState([]);
    const [loading, setLoading] = useState(false);
    const [view, setView]     = useState('table');

    const load = async () => {
        setLoading(true);
        setData([]);
        try {
            const r = await fetchFn();
            if (Number(r?.data?.code) === 200) {
                setData(Array.isArray(r.data.result) ? r.data.result : []);
            }
        } catch (e) {}
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <ViewToggle view={view} onChange={setView} />
                    {children && children(load)}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button icon={<ReloadOutlined />} size="small" onClick={load}>Refresh</Button>
                    <Button icon={<DownloadOutlined />} size="small" type="primary"
                        onClick={() => exportXlsx(data, exportName)} disabled={!data.length}>
                        Export XLSX
                    </Button>
                </div>
            </div>

            <Spin spinning={loading}>
                {view === 'table' ? (
                    <Table columns={columns} dataSource={data}
                        rowKey={(r, i) => r.id || i} size="small" scroll={{ x: 800 }}
                        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `${t} records` }}
                        locale={{ emptyText: loading ? 'Loading…' : 'No data found' }} />
                ) : (
                    data.length === 0
                        ? <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>No data to chart</div>
                        : renderCharts(data)
                )}
            </Spin>
        </div>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function FleetReports() {
    const [tab, setTab]           = useState('service-due');
    const [days, setDays]         = useState(30);
    const [months, setMonths]     = useState(12);
    const [minRepairs, setMinRepairs] = useState(3);

    // ── Column defs ────────────────────────────────────────────────────────────
    const serviceDueCols = [
        { title: 'Reg No',           dataIndex: 'registration_no',   key: 'rn', render: v => v || '—' },
        { title: 'Vehicle Type',     dataIndex: 'vehicle_type',      key: 'vt' },
        { title: 'Facility',         dataIndex: 'facility',          key: 'f'  },
        { title: 'Next Service Due', dataIndex: 'next_service_due',  key: 'nsd'},
        { title: 'Days Remaining',   dataIndex: 'days_remaining',    key: 'dr', render: v => <RAG val={Number(v)} okVal={30} warnVal={7} /> },
        { title: 'Driver',           dataIndex: 'assigned_driver',   key: 'ad', render: v => v || <Tag color="warning">Unassigned</Tag> },
        { title: 'Status',           dataIndex: 'functional_status', key: 'fs', render: v => <RAG val={v} /> },
    ];

    const lifespanCols = [
        { title: 'Reg No',       dataIndex: 'registration_no',  key: 'rn', render: v => v || '—' },
        { title: 'Vehicle Type', dataIndex: 'vehicle_type',     key: 'vt' },
        { title: 'Facility',     dataIndex: 'facility',         key: 'f'  },
        { title: 'Installed',    dataIndex: 'installation_date',key: 'id' },
        { title: 'End of Life',  dataIndex: 'end_of_life_date', key: 'eol'},
        { title: 'EOL Quarter',  key: 'eolq', render: r => r.eol_quarter ? `Q${r.eol_quarter} ${r.eol_year}` : '—' },
        { title: 'Days to EOL',  dataIndex: 'days_to_eol',      key: 'dte',render: v => <RAG val={Number(v)} okVal={730} warnVal={365} /> },
    ];

    const chronicCols = [
        { title: 'Reg No',            dataIndex: 'registration_no',  key: 'rn', render: v => v || '—' },
        { title: 'Vehicle Type',      dataIndex: 'vehicle_type',     key: 'vt' },
        { title: 'Facility',          dataIndex: 'facility',         key: 'f'  },
        { title: 'Repairs (period)',  dataIndex: 'repair_count',     key: 'rc', render: v => <Tag color="error">{v}</Tag> },
        { title: 'Total Repair Cost', dataIndex: 'total_repair_cost',key: 'trc',render: v => fmtE(v) },
        { title: 'First Repair',      dataIndex: 'first_repair_date',key: 'frd'},
        { title: 'Status',            dataIndex: 'functional_status',key: 'fs', render: v => <RAG val={v} /> },
    ];

    const driverCols = [
        { title: 'Reg No',        dataIndex: 'registration_no', key: 'rn', render: v => v || '—' },
        { title: 'Vehicle Type',  dataIndex: 'vehicle_type',    key: 'vt' },
        { title: 'Driver',        dataIndex: 'driver_name',     key: 'dn' },
        { title: 'Licence No',    dataIndex: 'licence_no',      key: 'ln', render: v => v || '—' },
        { title: 'Licence Expiry',dataIndex: 'licence_expiry',  key: 'le', render: v => v || '—' },
        { title: 'Assigned From', dataIndex: 'assigned_from',   key: 'af' },
        { title: 'Status',        dataIndex: 'status',          key: 's',  render: v => <Tag color={v === 'Active' ? 'success' : 'default'}>{v}</Tag> },
        { title: 'Facility',      dataIndex: 'facility',        key: 'fac'},
    ];

    const tcoCols = [
        { title: 'Reg No',         dataIndex: 'registration_no',  key: 'rn', render: v => v || '—' },
        { title: 'Vehicle Type',   dataIndex: 'vehicle_type',     key: 'vt' },
        { title: 'Facility',       dataIndex: 'facility',         key: 'f'  },
        { title: 'Procurement',    dataIndex: 'procurement_cost', key: 'pc', render: v => fmtE(v) },
        { title: 'Fuel Cost',      dataIndex: 'fuel_cost',        key: 'fc', render: v => fmtE(v) },
        { title: 'Repair Cost',    dataIndex: 'repair_cost',      key: 'rc', render: v => fmtE(v) },
        { title: 'Total Cost',     dataIndex: 'total_cost',       key: 'tc', render: v => <strong>{fmtE(v)}</strong> },
        { title: 'Total KM',       dataIndex: 'total_km',         key: 'km', render: v => fmtKm(v) },
        { title: 'Cost/km (E)',    key: 'cpk', render: r => toN(r.total_km) > 0 ? (toN(r.total_cost)/toN(r.total_km)).toFixed(2) : '—' },
    ];

    const documentCols = [
        { title: 'Reg No',        dataIndex: 'registration_no', key: 'rn', render: v => v || '—' },
        { title: 'Vehicle Type',  dataIndex: 'vehicle_type',    key: 'vt' },
        { title: 'Doc Type',      dataIndex: 'doc_type',        key: 'dt' },
        { title: 'Ref No',        dataIndex: 'reference_no',    key: 'ref',render: v => v || '—' },
        { title: 'Expiry Date',   dataIndex: 'expiry_date',     key: 'ed' },
        { title: 'Days Remaining',dataIndex: 'days_remaining',  key: 'dr', render: v => <RAG val={Number(v)} okVal={60} warnVal={14} /> },
        { title: 'Status',        dataIndex: 'status',          key: 's',  render: v => <RAG val={v} /> },
        { title: 'Facility',      dataIndex: 'facility',        key: 'fac'},
    ];

    const statusCols = [
        { title: 'Reg No',       dataIndex: 'registration_no',  key: 'rn', render: v => v || '—' },
        { title: 'Vehicle Type', dataIndex: 'vehicle_type',     key: 'vt' },
        { title: 'Facility',     dataIndex: 'facility',         key: 'f'  },
        { title: 'Status',       dataIndex: 'functional_status',key: 'fs', render: v => <RAG val={v} /> },
        { title: 'Driver',       dataIndex: 'assigned_driver',  key: 'ad', render: v => v || <Tag color="warning">Unassigned</Tag> },
        { title: 'Expired Docs', dataIndex: 'expired_docs',     key: 'exd',render: v => <RAG val={Number(v)} okVal={0} /> },
        { title: 'Expiring Docs',dataIndex: 'expiring_docs',    key: 'epd',render: v => <RAG val={Number(v)} okVal={0} /> },
        { title: 'Odometer (km)',dataIndex: 'current_odometer', key: 'odo',render: v => v ? fmtKm(v) : '—' },
    ];

    const fuelCols = [
        { title: 'Reg No',       dataIndex: 'registration_no',  key: 'rn', render: v => v || '—' },
        { title: 'Vehicle Type', dataIndex: 'vehicle_type',     key: 'vt' },
        { title: 'Month',        dataIndex: 'month',            key: 'm'  },
        { title: 'Litres',       dataIndex: 'total_litres',     key: 'l',  render: v => Number(v).toFixed(1) },
        { title: 'Cost',         dataIndex: 'total_cost',       key: 'c',  render: v => fmtE(v) },
        { title: 'KM Driven',    dataIndex: 'km_driven',        key: 'km', render: v => fmtKm(v) },
        { title: 'L/100km',      dataIndex: 'litres_per_100km', key: 'l100',render: v => Number(v).toFixed(2) },
    ];

    const ageingCols = [
        { title: 'Reg No',        dataIndex: 'registration_no',  key: 'rn', render: v => v || '—' },
        { title: 'Vehicle Type',  dataIndex: 'vehicle_type',     key: 'vt' },
        { title: 'Facility',      dataIndex: 'facility',         key: 'f'  },
        { title: 'End of Life',   dataIndex: 'end_of_life_date', key: 'eol'},
        { title: 'Days Past EOL', dataIndex: 'days_past_eol',    key: 'dpe',render: v => <Tag color="error">{v} days</Tag> },
        { title: 'Total Cost',    dataIndex: 'total_cost',       key: 'tc', render: v => fmtE(v) },
        { title: 'Status',        dataIndex: 'functional_status',key: 'fs', render: v => <RAG val={v} /> },
    ];

    // ── Chart renderers ────────────────────────────────────────────────────────

    const renderServiceDueCharts = (data) => {
        const countByFacility = Object.entries(
            data.reduce((m, r) => { m[r.facility] = (m[r.facility]||0)+1; return m; }, {})
        ).map(([name, count]) => ({ name, count })).sort((a,b) => b.count-a.count).slice(0,10);

        const byDays = [
            { range: 'Overdue', count: data.filter(r => toN(r.days_remaining) < 0).length, fill: C.red },
            { range: '0–7 days', count: data.filter(r => toN(r.days_remaining) >= 0 && toN(r.days_remaining) <= 7).length, fill: C.orange },
            { range: '8–30 days', count: data.filter(r => toN(r.days_remaining) > 7 && toN(r.days_remaining) <= 30).length, fill: C.amber },
        ].filter(d => d.count > 0);

        return (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <ChartCard title="Service Due — Urgency Breakdown">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byDays}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="range" tick={{ fontSize:11 }} />
                            <YAxis tick={{ fontSize:10 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="count" name="Vehicles" radius={[4,4,0,0]}>
                                {byDays.map((d,i) => <Cell key={i} fill={d.fill} />)}
                                <LabelList dataKey="count" position="top" style={{ fontSize:11 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Vehicles Due — by Facility (top 10)">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={countByFacility} layout="vertical"
                            margin={{ top:0, right:40, left:140, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:10 }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={135} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.orange} radius={[0,3,3,0]}>
                                <LabelList dataKey="count" position="right" style={{ fontSize:10 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        );
    };

    const renderLifespanCharts = (data) => {
        const byQuarter = Object.entries(
            data.reduce((m, r) => {
                const key = r.eol_quarter ? `Q${r.eol_quarter} ${r.eol_year}` : 'Unknown';
                m[key] = (m[key]||0)+1; return m;
            }, {})
        ).map(([q, count]) => ({ q, count }));

        const byType = Object.entries(
            data.reduce((m, r) => { m[r.vehicle_type||'Other'] = (m[r.vehicle_type||'Other']||0)+1; return m; }, {})
        ).map(([name, value]) => ({ name, value }));

        return (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <ChartCard title="End-of-Life by Quarter">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byQuarter}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="q" tick={{ fontSize:10 }} />
                            <YAxis tick={{ fontSize:10 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.purple} radius={[4,4,0,0]}>
                                <LabelList dataKey="count" position="top" style={{ fontSize:10 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Expiring Fleet — by Vehicle Type">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={byType} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={95} labelLine={false}
                                label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                                {byType.map((_,i) => <Cell key={i} fill={PIE_C[i%PIE_C.length]} />)}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={10} wrapperStyle={{ fontSize:10 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        );
    };

    const renderChronicCharts = (data) => {
        const top10 = [...data].sort((a,b) => toN(b.repair_count)-toN(a.repair_count)).slice(0,10)
            .map(r => ({ name: r.registration_no||'—', repairs: toN(r.repair_count), cost: toN(r.total_repair_cost) }));

        const byFacility = Object.entries(
            data.reduce((m, r) => { m[r.facility] = (m[r.facility]||0) + toN(r.total_repair_cost); return m; }, {})
        ).map(([name, cost]) => ({ name, cost })).sort((a,b) => b.cost-a.cost).slice(0,8);

        return (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <ChartCard title="Top 10 Chronic Vehicles — Repair Count">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={top10} layout="vertical"
                            margin={{ top:0, right:50, left:75, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:10 }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={70} />
                            <RTooltip formatter={(v, n) => [v, n === 'repairs' ? 'Repairs' : 'Cost (E)']} />
                            <Bar dataKey="repairs" fill={C.red} radius={[0,3,3,0]}>
                                <LabelList dataKey="repairs" position="right" style={{ fontSize:10 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Repair Cost by Facility (E)">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byFacility} layout="vertical"
                            margin={{ top:0, right:60, left:140, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:10 }} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={135} />
                            <RTooltip formatter={(v) => [`E ${v.toLocaleString()}`, 'Total Repair Cost']} />
                            <Bar dataKey="cost" fill={C.orange} radius={[0,3,3,0]}>
                                <LabelList dataKey="cost" position="right" style={{ fontSize:9 }}
                                    formatter={v => `E${(v/1000).toFixed(0)}k`} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        );
    };

    const renderDriverCharts = (data) => {
        const byStatus = Object.entries(
            data.reduce((m, r) => { m[r.status||'Unknown'] = (m[r.status||'Unknown']||0)+1; return m; }, {})
        ).map(([name, value]) => ({ name, value }));

        const byFacility = Object.entries(
            data.reduce((m, r) => { m[r.facility||'Unknown'] = (m[r.facility||'Unknown']||0)+1; return m; }, {})
        ).map(([name, count]) => ({ name, count })).sort((a,b) => b.count-a.count).slice(0,10);

        const expiring = data.filter(r => r.licence_expiry && r.licence_expiry !== '0000-00-00').map(r => {
            const days = Math.ceil((new Date(r.licence_expiry) - new Date()) / 86400000);
            return { ...r, days_left: days };
        }).filter(r => r.days_left <= 90).sort((a,b) => a.days_left - b.days_left).slice(0,10)
          .map(r => ({ name: r.driver_name||'—', days: r.days_left, reg: r.registration_no }));

        return (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <ChartCard title="Assignments by Status">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={byStatus} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={85}
                                label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                                {byStatus.map((_,i) => <Cell key={i} fill={PIE_C[i%PIE_C.length]} />)}
                            </Pie>
                            <RTooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Drivers per Facility">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byFacility} layout="vertical"
                            margin={{ top:0, right:35, left:130, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:10 }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={125} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.blue} radius={[0,3,3,0]}>
                                <LabelList dataKey="count" position="right" style={{ fontSize:10 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Licences Expiring (next 90 days)">
                    {expiring.length === 0 ? (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                            height:'100%', color:'#52c41a', fontWeight:600 }}>✓ No licences expiring soon</div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={expiring} layout="vertical"
                                margin={{ top:0, right:45, left:100, bottom:0 }}>
                                <XAxis type="number" tick={{ fontSize:10 }} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={95} />
                                <RTooltip formatter={(v) => [`${v} days`, 'Days Left']} />
                                <Bar dataKey="days" radius={[0,3,3,0]}>
                                    {expiring.map((d, i) => (
                                        <Cell key={i} fill={d.days <= 30 ? C.red : d.days <= 60 ? C.orange : C.amber} />
                                    ))}
                                    <LabelList dataKey="days" position="right" style={{ fontSize:10 }}
                                        formatter={v => `${v}d`} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>
        );
    };

    const renderTCOCharts = (data) => {
        const top10 = [...data].sort((a,b) => toN(b.total_cost)-toN(a.total_cost)).slice(0,10)
            .map(r => ({
                name: r.registration_no||'—',
                Procurement: toN(r.procurement_cost),
                Fuel: toN(r.fuel_cost),
                Repair: toN(r.repair_cost),
            }));

        const costPerKm = [...data].filter(r => toN(r.total_km)>0)
            .map(r => ({ name: r.registration_no||'—', cpk: +(toN(r.total_cost)/toN(r.total_km)).toFixed(2) }))
            .sort((a,b) => b.cpk-a.cpk).slice(0,10);

        return (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <ChartCard title="Top 10 — Cost Breakdown (E)" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={top10} layout="vertical"
                            margin={{ top:0, right:10, left:70, bottom:0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize:9 }} tickFormatter={v => `E${(v/1000).toFixed(0)}k`} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={65} />
                            <RTooltip formatter={(v, n) => [`E ${v.toLocaleString()}`, n]} />
                            <Legend wrapperStyle={{ fontSize:10 }} />
                            <Bar dataKey="Procurement" stackId="a" fill={C.blue} />
                            <Bar dataKey="Fuel"        stackId="a" fill={C.green} />
                            <Bar dataKey="Repair"      stackId="a" fill={C.red} radius={[0,3,3,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Top 10 — Cost per KM (E/km)" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={costPerKm} layout="vertical"
                            margin={{ top:0, right:50, left:70, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:10 }} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={65} />
                            <RTooltip formatter={(v) => [`E ${v}/km`, 'Cost per KM']} />
                            <Bar dataKey="cpk" fill={C.orange} radius={[0,3,3,0]}>
                                <LabelList dataKey="cpk" position="right" style={{ fontSize:9 }}
                                    formatter={v => `E${v}`} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        );
    };

    const renderDocCharts = (data) => {
        const byStatus = Object.entries(
            data.reduce((m, r) => { m[r.status||'Unknown'] = (m[r.status||'Unknown']||0)+1; return m; }, {})
        ).map(([name, value]) => ({ name, value }));

        const byDocType = Object.entries(
            data.reduce((m, r) => { m[r.doc_type||'Other'] = (m[r.doc_type||'Other']||0)+1; return m; }, {})
        ).map(([name, count]) => ({ name, count })).sort((a,b) => b.count-a.count);

        const expiringSoon = data.filter(r => toN(r.days_remaining) >= 0 && toN(r.days_remaining) <= 60)
            .map(r => ({ name: `${r.registration_no} (${r.doc_type})`, days: toN(r.days_remaining) }))
            .sort((a,b) => a.days-b.days).slice(0,10);

        return (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <ChartCard title="Documents by Status">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={byStatus} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={85}
                                label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                                {byStatus.map((d,i) => (
                                    <Cell key={i} fill={d.name==='Expired'?C.red:d.name==='Expiring Soon'?C.amber:C.green} />
                                ))}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={9} wrapperStyle={{ fontSize:10 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="By Document Type">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byDocType}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={{ fontSize:9 }} />
                            <YAxis tick={{ fontSize:10 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.blue} radius={[4,4,0,0]}>
                                <LabelList dataKey="count" position="top" style={{ fontSize:10 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Expiring in 60 Days">
                    {expiringSoon.length === 0 ? (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                            height:'100%', color:'#52c41a', fontWeight:600 }}>✓ No docs expiring soon</div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={expiringSoon} layout="vertical"
                                margin={{ top:0, right:40, left:135, bottom:0 }}>
                                <XAxis type="number" tick={{ fontSize:10 }} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize:9 }} width={130} />
                                <RTooltip formatter={(v) => [`${v} days`, 'Days Left']} />
                                <Bar dataKey="days" radius={[0,3,3,0]}>
                                    {expiringSoon.map((d,i) => (
                                        <Cell key={i} fill={d.days<=14?C.red:d.days<=30?C.orange:C.amber} />
                                    ))}
                                    <LabelList dataKey="days" position="right" style={{ fontSize:9 }}
                                        formatter={v => `${v}d`} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>
        );
    };

    const renderStatusCharts = (data) => {
        const byStatus = Object.entries(
            data.reduce((m, r) => { m[r.functional_status||'Unknown'] = (m[r.functional_status||'Unknown']||0)+1; return m; }, {})
        ).map(([name, value]) => ({ name, value }));

        const unassigned = data.filter(r => !r.assigned_driver).length;
        const assigned   = data.length - unassigned;
        const expDocs    = data.filter(r => toN(r.expired_docs) > 0).length;

        const byFacility = Object.entries(
            data.reduce((m, r) => { m[r.facility||'Other'] = (m[r.facility||'Other']||0)+1; return m; }, {})
        ).map(([name, count]) => ({ name, count })).sort((a,b) => b.count-a.count).slice(0,8);

        return (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <ChartCard title="Fleet by Functional Status">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={byStatus} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={90}
                                label={({ name, percent }) => `${(percent*100).toFixed(0)}%`}>
                                {byStatus.map((d,i) => {
                                    const fill = /good|service|function/i.test(d.name) ? C.green
                                        : /repair|unservice/i.test(d.name) ? C.orange
                                        : /board|dispos/i.test(d.name) ? C.red : PIE_C[i%PIE_C.length];
                                    return <Cell key={i} fill={fill} />;
                                })}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={9} wrapperStyle={{ fontSize:10 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Assignment & Document Summary">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[
                            { label: 'Assigned', value: assigned,   fill: C.green },
                            { label: 'Unassigned', value: unassigned, fill: C.amber },
                            { label: 'Expired Docs', value: expDocs,   fill: C.red },
                        ]}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="label" tick={{ fontSize:11 }} />
                            <YAxis tick={{ fontSize:10 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="value" radius={[4,4,0,0]}>
                                {[{fill:C.green},{fill:C.amber},{fill:C.red}].map((d,i) =>
                                    <Cell key={i} fill={d.fill} />)}
                                <LabelList dataKey="value" position="top" style={{ fontSize:11 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Fleet Count by Facility (top 8)">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byFacility} layout="vertical"
                            margin={{ top:0, right:35, left:130, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:10 }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={125} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.blue} radius={[0,3,3,0]}>
                                <LabelList dataKey="count" position="right" style={{ fontSize:10 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        );
    };

    const renderFuelCharts = (data) => {
        const byMonth = Object.entries(
            data.reduce((m, r) => {
                if (!m[r.month]) m[r.month] = { month: r.month, litres: 0, cost: 0, km: 0 };
                m[r.month].litres += toN(r.total_litres);
                m[r.month].cost   += toN(r.total_cost);
                m[r.month].km     += toN(r.km_driven);
                return m;
            }, {})
        ).map(([, v]) => v).sort((a,b) => a.month > b.month ? 1 : -1);

        const topConsumers = Object.entries(
            data.reduce((m, r) => {
                m[r.registration_no||'—'] = (m[r.registration_no||'—']||0) + toN(r.total_litres);
                return m;
            }, {})
        ).map(([name, litres]) => ({ name, litres })).sort((a,b)=>b.litres-a.litres).slice(0,8);

        return (
            <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:12 }}>
                <ChartCard title="Fuel Consumption & Cost — Monthly">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byMonth} margin={{ top:0, right:10, left:0, bottom:0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" tick={{ fontSize:10 }} />
                            <YAxis yAxisId="l" tick={{ fontSize:10 }} />
                            <YAxis yAxisId="c" orientation="right" tick={{ fontSize:10 }}
                                tickFormatter={v => `E${(v/1000).toFixed(0)}k`} />
                            <RTooltip formatter={(v, n) => [
                                n === 'litres' ? `${v.toFixed(0)} L` : `E ${v.toLocaleString()}`, n
                            ]} />
                            <Legend wrapperStyle={{ fontSize:10 }} />
                            <Bar yAxisId="l" dataKey="litres" fill={C.blue} name="Litres" radius={[3,3,0,0]} />
                            <Bar yAxisId="c" dataKey="cost"   fill={C.green} name="Cost (E)" radius={[3,3,0,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Top 8 Fuel Consumers (Litres)">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={topConsumers} layout="vertical"
                            margin={{ top:0, right:45, left:70, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:10 }} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={65} />
                            <RTooltip formatter={(v) => [`${v.toFixed(1)} L`, 'Total Litres']} />
                            <Bar dataKey="litres" fill={C.orange} radius={[0,3,3,0]}>
                                <LabelList dataKey="litres" position="right" style={{ fontSize:9 }}
                                    formatter={v => `${v.toFixed(0)}L`} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        );
    };

    const renderAgeingCharts = (data) => {
        const byFacility = Object.entries(
            data.reduce((m, r) => { m[r.facility||'Other'] = (m[r.facility||'Other']||0)+1; return m; }, {})
        ).map(([name, count]) => ({ name, count })).sort((a,b) => b.count-a.count).slice(0,8);

        const byType = Object.entries(
            data.reduce((m, r) => { m[r.vehicle_type||'Other'] = (m[r.vehicle_type||'Other']||0)+1; return m; }, {})
        ).map(([name, value]) => ({ name, value }));

        return (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <ChartCard title="Ageing Vehicles by Facility">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={byFacility} layout="vertical"
                            margin={{ top:0, right:40, left:140, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:10 }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={135} />
                            <RTooltip />
                            <Bar dataKey="count" fill={C.red} radius={[0,3,3,0]}>
                                <LabelList dataKey="count" position="right" style={{ fontSize:10 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Ageing Fleet by Vehicle Type">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={byType} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={95}
                                label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                                {byType.map((_,i) => <Cell key={i} fill={PIE_C[i%PIE_C.length]} />)}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={9} wrapperStyle={{ fontSize:10 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        );
    };

    return (
        <div className="page-content" style={{ padding: '20px 24px' }}>
            <div style={{ marginBottom: 16 }}>
                <h4 style={{ margin: 0, fontWeight: 700 }}>Fleet Reports</h4>
                <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                    Toggle between table and chart view on each tab
                </div>
            </div>

            <Tabs activeKey={tab} onChange={setTab} size="small" destroyInactiveTabPane>

                <TabPane tab="Service Due" key="service-due">
                    <ReportTab fetchFn={() => FleetService.reportServiceDue(days)}
                        columns={serviceDueCols} exportName="Service_Due"
                        renderCharts={renderServiceDueCharts}>
                        {(reload) => (
                            <>
                                <span style={{ fontSize: 12 }}>Days ahead:</span>
                                <InputNumber min={7} max={365} value={days} size="small" style={{ width: 80 }}
                                    onChange={v => { setDays(v); setTimeout(reload, 100); }} />
                            </>
                        )}
                    </ReportTab>
                </TabPane>

                <TabPane tab="Lifespan" key="lifespan">
                    <ReportTab fetchFn={() => FleetService.reportLifespan()}
                        columns={lifespanCols} exportName="Lifespan_Expiry"
                        renderCharts={renderLifespanCharts} />
                </TabPane>

                <TabPane tab="Chronic Breakdowns" key="chronic">
                    <ReportTab fetchFn={() => FleetService.reportChronic(months, minRepairs)}
                        columns={chronicCols} exportName="Chronic_Breakdowns"
                        renderCharts={renderChronicCharts}>
                        {(reload) => (
                            <>
                                <span style={{ fontSize: 12 }}>Months:</span>
                                <InputNumber min={1} max={24} value={months} size="small" style={{ width: 70 }}
                                    onChange={v => { setMonths(v); setTimeout(reload, 100); }} />
                                <span style={{ fontSize: 12, marginLeft: 8 }}>Min repairs:</span>
                                <InputNumber min={1} max={20} value={minRepairs} size="small" style={{ width: 70 }}
                                    onChange={v => { setMinRepairs(v); setTimeout(reload, 100); }} />
                            </>
                        )}
                    </ReportTab>
                </TabPane>

                <TabPane tab="Driver Assignments" key="drivers">
                    <ReportTab fetchFn={() => FleetService.reportDrivers()}
                        columns={driverCols} exportName="Driver_Assignments"
                        renderCharts={renderDriverCharts} />
                </TabPane>

                <TabPane tab="Cost of Ownership" key="tco">
                    <ReportTab fetchFn={() => FleetService.reportTCO()}
                        columns={tcoCols} exportName="Total_Cost_Ownership"
                        renderCharts={renderTCOCharts} />
                </TabPane>

                <TabPane tab="Documents" key="documents">
                    <ReportTab fetchFn={() => FleetService.reportDocs()}
                        columns={documentCols} exportName="Document_Compliance"
                        renderCharts={renderDocCharts} />
                </TabPane>

                <TabPane tab="Fleet Status" key="status">
                    <ReportTab fetchFn={() => FleetService.reportStatus()}
                        columns={statusCols} exportName="Fleet_Status"
                        renderCharts={renderStatusCharts} />
                </TabPane>

                <TabPane tab="Fuel" key="fuel">
                    <ReportTab fetchFn={() => FleetService.reportFuel()}
                        columns={fuelCols} exportName="Fuel_Consumption"
                        renderCharts={renderFuelCharts} />
                </TabPane>

                <TabPane tab="Ageing Fleet" key="ageing">
                    <ReportTab fetchFn={() => FleetService.reportAgeing()}
                        columns={ageingCols} exportName="Ageing_Fleet"
                        renderCharts={renderAgeingCharts} />
                </TabPane>

            </Tabs>
        </div>
    );
}